import { useEffect, useState } from 'react'

import CardSurface from '../card-surface/CardSurface'
import Button from '../button/Button'
import { ResidentLeaseOffer } from '../../_types/resident'
import {
  getMyLeaseOffers,
  respondToLeaseOffer,
} from '../../_data/resident'
import {
  ActionRow,
  AgreeText,
  ErrorText,
  OfferDetail,
  OfferHeading,
  OfferStack,
  SignBox,
  SignInput,
  SignLabel,
  StatusText,
  TermsText,
} from './styled'

interface LeaseRenewalSectionProps {
  tenancyId: string
}

const formatMoney = (cents: number, currency: string): string =>
  `$${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`

const formatDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

/**
 * The resident-facing lease renewal card. Shows any PENDING offer the property
 * manager has sent, lets the resident review the terms and either e-sign (type
 * their legal name → accept) or decline. Renders nothing when there's no pending
 * offer, so it only appears when there's something to act on.
 *
 * @param {LeaseRenewalSectionProps} props - the resident's tenancy.
 * @returns {JSX.Element | null}
 */
const LeaseRenewalSection = ({
  tenancyId,
}: LeaseRenewalSectionProps): JSX.Element | null => {
  const [pendingOffers, setPendingOffers] = useState<ResidentLeaseOffer[]>([])
  const [signingId, setSigningId] = useState<string | null>(null)
  const [signerName, setSignerName] = useState<string>('')
  const [isBusy, setIsBusy] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [signedMessage, setSignedMessage] = useState<string>('')

  const loadOffers = (): void => {
    void getMyLeaseOffers(tenancyId).then(offers => {
      setPendingOffers(offers.filter(offer => offer.status === 'sent'))
    })
  }

  useEffect(() => {
    loadOffers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenancyId])

  const handleAccept = (offer: ResidentLeaseOffer): void => {
    if (signerName.trim() === '' || isBusy === true) {
      return
    }
    setIsBusy(true)
    setErrorMessage('')
    void respondToLeaseOffer(offer.id, 'accept', signerName.trim()).then(
      result => {
        setIsBusy(false)
        if (result === null) {
          setErrorMessage('We couldn’t sign that — please try again.')
          return
        }
        setSigningId(null)
        setSignerName('')
        setSignedMessage('Signed — your renewal is confirmed.')
        loadOffers()
      },
    )
  }

  const handleDecline = (offer: ResidentLeaseOffer): void => {
    if (isBusy === true) {
      return
    }
    setIsBusy(true)
    setErrorMessage('')
    void respondToLeaseOffer(offer.id, 'decline', '').then(result => {
      setIsBusy(false)
      if (result === null) {
        setErrorMessage('That didn’t work — please try again.')
        return
      }
      loadOffers()
    })
  }

  if (pendingOffers.length === 0) {
    if (signedMessage !== '') {
      return (
        <CardSurface padding="standard">
          <StatusText>{signedMessage}</StatusText>
        </CardSurface>
      )
    }
    return null
  }

  return (
    <CardSurface padding="standard">
      <OfferStack>
        <OfferHeading>Lease renewal offer</OfferHeading>
        {pendingOffers.map(offer => (
          <OfferStack key={offer.id}>
            <OfferDetail>
              {formatMoney(offer.newRentCents, offer.currency)}/month
            </OfferDetail>
            <OfferDetail>
              {formatDate(offer.newLeaseStartDate)} –{' '}
              {formatDate(offer.newLeaseEndDate)}
            </OfferDetail>
            {offer.termsText !== '' ? (
              <TermsText>{offer.termsText}</TermsText>
            ) : null}

            {signingId === offer.id ? (
              <SignBox>
                <SignLabel>Type your full legal name to sign</SignLabel>
                <SignInput
                  value={signerName}
                  placeholder="Your full name"
                  onChange={event => setSignerName(event.target.value)}
                />
                <AgreeText>
                  By signing, I agree to renew my lease under the terms above.
                </AgreeText>
                <ActionRow>
                  <Button
                    type="button"
                    variant="primary"
                    size="small"
                    disabled={isBusy === true || signerName.trim() === ''}
                    onClick={() => handleAccept(offer)}
                  >
                    Accept &amp; sign
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="small"
                    disabled={isBusy === true}
                    onClick={() => setSigningId(null)}
                  >
                    Cancel
                  </Button>
                </ActionRow>
              </SignBox>
            ) : (
              <ActionRow>
                <Button
                  type="button"
                  variant="primary"
                  size="small"
                  onClick={() => {
                    setSigningId(offer.id)
                    setSignerName('')
                  }}
                >
                  Review &amp; sign
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  disabled={isBusy === true}
                  onClick={() => handleDecline(offer)}
                >
                  Decline
                </Button>
              </ActionRow>
            )}
          </OfferStack>
        ))}
        {errorMessage !== '' ? <ErrorText>{errorMessage}</ErrorText> : null}
      </OfferStack>
    </CardSurface>
  )
}

export default LeaseRenewalSection
